"use client";
import React from "react";
import Button from "@/components/shared/Button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import styles from "./genericCtaBlock.module.css";

export type GenericCtaBlockButton = {
  text?: string;
  href?: string;
  variant?: "primary" | "secondary";
};

export type GenericCtaBlockProps = {
  title?: string;
  boldParagraph?: string;
  regularParagraph?: string;
  buttons?: GenericCtaBlockButton[];
  backgroundColor?: "purple" | "blue" | "green" | "transparent";
  isLastSection?: boolean;
};

const GenericCtaBlock: React.FC<GenericCtaBlockProps> = ({
  title,
  boldParagraph,
  regularParagraph,
  buttons = [],
  backgroundColor = "purple",
  isLastSection = false,
}) => {
  const [sectionRef, isVisible] = useScrollAnimation();
  
  // Ensure title has a default value
  const displayTitle = title || "Outcome";
  
  // Ensure buttons is an array
  const displayButtons = Array.isArray(buttons) ? buttons : [];
  
  const backgroundColorClasses = {
    purple: "bg-[#a78bfa33] border-[#a78bfa80]",
    blue: "bg-[#3b82f633] border-[#3b82f680]",
    green: "bg-[#10b98133] border-[#10b98180]",
    transparent: "bg-transparent border-transparent",
  };

  const bgClass = backgroundColorClasses[backgroundColor] || backgroundColorClasses.purple;
  
  // Use pb-20 for last section, pb-12 otherwise
  const paddingBottom = isLastSection ? "pb-20" : "pb-12";

  // Always render the section - it should be visible even with minimal content
  return (
    <section 
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`flex flex-col w-full pt-12 ${paddingBottom} items-center justify-center ${isVisible ? styles.sectionAnimation : ""}`}
    >
      <div className={`flex flex-col w-full items-center justify-center max-w-[1025px] h-auto min-h-[409px] gap-6 px-8 md:px-[127px] py-8 md:py-[38px] relative ${bgClass} rounded-[10px] border border-solid`}
        aria-label="Call to action section" >
    
        <h2 className={`relative flex items-center justify-center w-full max-w-[763px] h-auto [font-family:'Assistant-SemiBold',Helvetica] font-semibold text-white text-2xl md:text-[32px] text-center tracking-[0] leading-[36px] md:leading-[46px] ${isVisible ? styles.headerAnimation : ""}`}>
          {displayTitle}
        </h2>

        {(boldParagraph || regularParagraph) && (
          <div className={`flex flex-col h-auto items-start gap-2 w-full max-w-[763px] ${isVisible ? styles.contentAnimation : ""}`}>
            {boldParagraph && (
              <p className="relative self-stretch [font-family:'Inter-Bold',Helvetica] font-bold text-white text-base text-center tracking-[0] leading-6">
                {boldParagraph}
              </p>
            )}

            {regularParagraph && (
              <p className="relative self-stretch [font-family:'Inter-Regular',Helvetica] font-normal text-slate-300 text-base text-center tracking-[0] leading-6">
                {regularParagraph}
              </p>
            )}
          </div>
        )}

        {displayButtons.length > 0 && (
          <div className={`flex flex-wrap w-full max-w-[370px] items-center justify-center gap-[15px] relative flex-[0_0_auto] ${isVisible ? styles.buttonsAnimation : ""}`}>
            {displayButtons.map((button, index) => (
              <Button
                key={index}
                href={button.href}
                variant={button.variant || "primary"}
                ariaLabel={button.text || `Button ${index + 1}`}
              >
                {button.text}
              </Button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default GenericCtaBlock;

